import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { inventoryService } from '../services/api';
import Navbar from '../components/Navbar';

const fmt = n => '₡' + Number(n).toLocaleString('es-CR');
const emptyProduct = () => ({ name:'', category_id:'', description:'' });
const emptyVariant = () => ({ name:'', price:'', stock:'', stock_min:1, sku:'' });

export default function Inventory() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('Todos');
  const [stockModal, setStockModal] = useState(null);
  const [priceModal, setPriceModal] = useState(null);
  const [newProdModal, setNewProdModal] = useState(false);
  const [newVariantModal, setNewVariantModal] = useState(null);
  const [qty, setQty] = useState('');
  const [tipo, setTipo] = useState('entrada');
  const [newPrice, setNewPrice] = useState('');
  const [prodForm, setProdForm] = useState(emptyProduct());
  const [variantForm, setVariantForm] = useState(emptyVariant());
  const [variants, setVariants] = useState([emptyVariant()]);

  const load = async () => {
    try {
      const calls = [inventoryService.getProducts(), inventoryService.getCategories()];
      if (user.role !== 'vendedor') calls.push(inventoryService.getAlerts());
      const results = await Promise.all(calls);
      setProducts(results[0].data);
      setCategories(results[1].data);
      if (user.role !== 'vendedor') setAlerts(results[2].data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const applyMovement = async () => {
    const q = parseInt(qty);
    if (!q || q <= 0) return;
    try {
      await inventoryService.addMovement({ variant_id:stockModal.varId, type:tipo, quantity:q, reason:tipo });
      await load();
      setStockModal(null); setQty(''); setTipo('entrada');
    } catch (err) { alert('Error al registrar movimiento'); }
  };

  const applyPriceEdit = async () => {
    if (!newPrice || Number(newPrice) <= 0) return;
    try {
      await inventoryService.updateVariant(priceModal.varId, { price:Number(newPrice), cost:priceModal.cost||0, stock_min:priceModal.stockMin, sku:priceModal.sku });
      await load();
      setPriceModal(null); setNewPrice('');
    } catch (err) { alert('Error al actualizar precio'); }
  };

  const createProduct = async () => {
    if (!prodForm.name || !prodForm.category_id) { alert('Nombre y categoría son obligatorios'); return; }
    const validVariants = variants.filter(v => v.name && v.price);
    if (validVariants.length === 0) { alert('Agrega al menos una variante con nombre y precio'); return; }
    try {
      const res = await inventoryService.createProduct(prodForm);
      for (const v of validVariants) {
        await inventoryService.createVariant({ ...v, product_id:res.data.id, price:Number(v.price), stock:Number(v.stock)||0, stock_min:Number(v.stock_min)||1 });
      }
      await load();
      setNewProdModal(false); setProdForm(emptyProduct()); setVariants([emptyVariant()]);
    } catch (err) { alert('Error al crear producto'); }
  };

  const createVariant = async () => {
    if (!variantForm.name || !variantForm.price) { alert('Nombre y precio son obligatorios'); return; }
    try {
      await inventoryService.createVariant({ ...variantForm, product_id:newVariantModal.prodId, price:Number(variantForm.price), stock:Number(variantForm.stock)||0, stock_min:Number(variantForm.stock_min)||1 });
      await load();
      setNewVariantModal(null); setVariantForm(emptyVariant());
    } catch (err) { alert('Error al crear variante'); }
  };

  const deactivateVariant = async (varId) => {
    if (!window.confirm('¿Desactivar esta variante? No aparecerá en el catálogo.')) return;
    try {
      await inventoryService.updateVariant(varId, { active:false });
      await load();
    } catch (err) { alert('Error al desactivar'); }
  };

  const deleteProduct = async (prod) => {
    if (!window.confirm(`¿Eliminar permanentemente "${prod.name}" con todas sus variantes?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await inventoryService.deleteProduct(prod.id);
      await load();
    } catch (err) { alert('Error al eliminar producto'); }
  };

  const cats = ['Todos', ...new Set(products.map(p => p.categoria).filter(Boolean))];
  const filtered = catFilter === 'Todos' ? products : products.filter(p => p.categoria === catFilter);

  const s = {
    main: { maxWidth:1100, margin:'0 auto', padding:'20px 14px' },
    card: { background:'#fff', borderRadius:10, boxShadow:'0 1px 6px rgba(0,0,0,0.09)', padding:22, marginBottom:18 },
    table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
    th: { background:'#f5ede6', padding:'9px 11px', textAlign:'left', fontWeight:600, color:'#5c3d2e', borderBottom:'2px solid #e0d0c0' },
    td: { padding:'9px 11px', borderBottom:'1px solid #f0e8e0', verticalAlign:'middle' },
    btn: (bg, color) => ({ background:bg||'#f0ebe5', color:color||'#5c3d2e', border:'none', padding:'5px 10px', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:600, marginRight:4 }),
    stock: (s,m) => ({ background:s===0?'#ef4444':s<=m?'#f59e0b':'#10b981', color:'#fff', padding:'2px 8px', borderRadius:12, fontSize:11, fontWeight:600 }),
    alert: { background:'#fff3cd', border:'1px solid #ffc107', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13 },
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100 },
    box: (w) => ({ background:'#fff', borderRadius:12, padding:26, maxWidth:w||380, width:'90%', maxHeight:'85vh', overflowY:'auto' }),
    input: { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:7, fontSize:14, boxSizing:'border-box', marginBottom:12 },
    select: { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:7, fontSize:14, boxSizing:'border-box', marginBottom:12 },
    label: { display:'block', marginBottom:4, fontWeight:600, fontSize:13, color:'#555' },
    grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' },
    btnPrimary: (c) => ({ background:c||'#5c3d2e', color:'#fff', border:'none', padding:'10px 24px', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }),
    btnSec: { background:'#f0ebe5', color:'#5c3d2e', border:'none', padding:'10px 18px', borderRadius:7, cursor:'pointer', fontSize:13, marginLeft:8 },
  };

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", minHeight:'100vh', background:'#f8f5f0' }}>
      <Navbar />
      <div style={s.main}>
        {alerts.length > 0 && (
          <div style={s.alert}>⚠️ <strong>Stock bajo:</strong> {alerts.map(a=>`${a.producto} ${a.name} (${a.stock} uds)`).join(' · ')}</div>
        )}
        <div style={s.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h2 style={{ margin:0, color:'#5c3d2e', fontSize:18 }}>
              📦 Inventario {user.role==='vendedor' && <span style={{ fontSize:12, color:'#aaa', fontWeight:400 }}>(solo lectura)</span>}
            </h2>
            {user.role !== 'vendedor' && (
              <button style={s.btnPrimary()} onClick={()=>setNewProdModal(true)}>➕ Nuevo Producto</button>
            )}
          </div>
          <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
            {cats.map(c => (
              <button key={c} onClick={()=>setCatFilter(c)}
                style={{ background:catFilter===c?'#5c3d2e':'#f0ebe5', color:catFilter===c?'#fff':'#5c3d2e', border:'none', padding:'6px 14px', borderRadius:7, cursor:'pointer', fontSize:12 }}>
                {c}
              </button>
            ))}
          </div>
          {loading
            ? <div style={{ color:'#aaa', textAlign:'center', padding:28 }}>Cargando inventario...</div>
            : filtered.map(prod => (
              <div key={prod.id} style={{ marginBottom:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, paddingBottom:6, borderBottom:'1px solid #f0e8e0' }}>
                  <div style={{ fontWeight:700, fontSize:15, color:'#5c3d2e' }}>
                    {prod.name} <span style={{ fontSize:12, color:'#aaa', fontWeight:400 }}>— {prod.categoria}</span>
                  </div>
                  {user.role !== 'vendedor' && (
                    <div style={{ display:'flex', gap:4 }}>
                      <button style={s.btn('#e8f4fd','#3b82f6')} onClick={()=>setNewVariantModal({prodId:prod.id,prodNom:prod.name})}>➕ Variante</button>
                      <button style={s.btn('#fee2e2','#ef4444')} onClick={()=>deleteProduct(prod)}>🗑️ Eliminar</button>
                    </div>
                  )}
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>Variante</th>
                      <th style={s.th}>Precio</th>
                      <th style={s.th}>Stock</th>
                      <th style={s.th}>Mín.</th>
                      {user.role !== 'vendedor' && <th style={s.th}>Acciones</th>}
                    </tr></thead>
                    <tbody>
                      {prod.variantes?.map(v => (
                        <tr key={v.id}>
                          <td style={s.td}>{v.nombre}</td>
                          <td style={s.td}>{fmt(v.precio)}</td>
                          <td style={s.td}><span style={s.stock(v.stock,v.stockMin)}>{v.stock} uds</span></td>
                          <td style={s.td}>{v.stockMin} uds</td>
                          {user.role !== 'vendedor' && (
                            <td style={s.td}>
                              <button style={s.btn('#f0fdf4','#10b981')} onClick={()=>setStockModal({varId:v.id,prodNom:prod.name,varNom:v.nombre,stock:v.stock})}>± Stock</button>
                              <button style={s.btn('#e8f4fd','#3b82f6')} onClick={()=>{setPriceModal({varId:v.id,prodNom:prod.name,varNom:v.nombre,precio:v.precio,cost:v.costo||0,stockMin:v.stockMin,sku:v.sku});setNewPrice(v.precio);}}>✏️ Precio</button>
                              <button style={s.btn('#fee2e2','#ef4444')} onClick={()=>deactivateVariant(v.id)}>🚫 Desactivar</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {stockModal && (
        <div style={s.overlay} onClick={()=>setStockModal(null)}>
          <div style={s.box()} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 16px', color:'#5c3d2e' }}>± Ajustar Stock</h3>
            <div style={{ fontSize:14, marginBottom:14 }}><strong>{stockModal.prodNom}</strong> — {stockModal.varNom}<br/><span style={{ color:'#888' }}>Stock actual: {stockModal.stock} uds</span></div>
            <label style={s.label}>Tipo</label>
            <select style={s.select} value={tipo} onChange={e=>setTipo(e.target.value)}>
              <option value="entrada">📥 Entrada (agregar)</option>
              <option value="salida">📤 Salida (restar)</option>
            </select>
            <label style={s.label}>Cantidad</label>
            <input style={s.input} type="number" min="1" placeholder="Ej: 5" value={qty} onChange={e=>setQty(e.target.value)}/>
            <div style={{ display:'flex', gap:10 }}>
              <button style={s.btnPrimary()} onClick={applyMovement}>✅ Aplicar</button>
              <button style={s.btnSec} onClick={()=>setStockModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {priceModal && (
        <div style={s.overlay} onClick={()=>setPriceModal(null)}>
          <div style={s.box()} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 16px', color:'#3b82f6' }}>✏️ Editar Precio</h3>
            <div style={{ fontSize:14, marginBottom:14 }}><strong>{priceModal.prodNom}</strong> — {priceModal.varNom}<br/><span style={{ color:'#888' }}>Precio actual: {fmt(priceModal.precio)}</span></div>
            <label style={s.label}>Nuevo Precio (₡)</label>
            <input style={s.input} type="number" min="1" value={newPrice} onChange={e=>setNewPrice(e.target.value)}/>
            <div style={{ display:'flex', gap:10 }}>
              <button style={s.btnPrimary('#3b82f6')} onClick={applyPriceEdit}>✅ Guardar</button>
              <button style={s.btnSec} onClick={()=>setPriceModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {newVariantModal && (
        <div style={s.overlay} onClick={()=>setNewVariantModal(null)}>
          <div style={s.box(460)} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 6px', color:'#5c3d2e' }}>➕ Nueva Variante</h3>
            <div style={{ fontSize:13, color:'#888', marginBottom:16 }}>{newVariantModal.prodNom}</div>
            <div style={s.grid2}>
              <div><label style={s.label}>Nombre *</label><input style={s.input} placeholder="Ej: Queen" value={variantForm.name} onChange={e=>setVariantForm(p=>({...p,name:e.target.value}))}/></div>
              <div><label style={s.label}>Precio (₡) *</label><input style={s.input} type="number" placeholder="150000" value={variantForm.price} onChange={e=>setVariantForm(p=>({...p,price:e.target.value}))}/></div>
            </div>
            <div style={s.grid2}>
              <div><label style={s.label}>Stock inicial</label><input style={s.input} type="number" placeholder="0" value={variantForm.stock} onChange={e=>setVariantForm(p=>({...p,stock:e.target.value}))}/></div>
              <div><label style={s.label}>Stock mínimo</label><input style={s.input} type="number" placeholder="1" value={variantForm.stock_min} onChange={e=>setVariantForm(p=>({...p,stock_min:e.target.value}))}/></div>
            </div>
            <label style={s.label}>SKU</label>
            <input style={s.input} placeholder="Ej: COL-QUE" value={variantForm.sku} onChange={e=>setVariantForm(p=>({...p,sku:e.target.value}))}/>
            <div style={{ display:'flex', gap:10 }}>
              <button style={s.btnPrimary()} onClick={createVariant}>✅ Agregar Variante</button>
              <button style={s.btnSec} onClick={()=>setNewVariantModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {newProdModal && (
        <div style={s.overlay} onClick={()=>setNewProdModal(false)}>
          <div style={s.box(560)} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 16px', color:'#5c3d2e' }}>➕ Nuevo Producto</h3>
            <label style={s.label}>Nombre del producto *</label>
            <input style={s.input} placeholder="Ej: Colchón Premium" value={prodForm.name} onChange={e=>setProdForm(p=>({...p,name:e.target.value}))}/>
            <label style={s.label}>Categoría *</label>
            <select style={s.select} value={prodForm.category_id} onChange={e=>setProdForm(p=>({...p,category_id:e.target.value}))}>
              <option value="">Selecciona una categoría</option>
              {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label style={s.label}>Descripción</label>
            <input style={s.input} placeholder="Descripción opcional" value={prodForm.description} onChange={e=>setProdForm(p=>({...p,description:e.target.value}))}/>
            <div style={{ borderTop:'1px solid #f0e8e0', paddingTop:14, marginTop:4, marginBottom:10 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <strong style={{ fontSize:14, color:'#5c3d2e' }}>Variantes</strong>
                <button style={s.btn('#e8f4fd','#3b82f6')} onClick={()=>setVariants(p=>[...p,emptyVariant()])}>➕ Agregar variante</button>
              </div>
              {variants.map((v,i)=>(
                <div key={i} style={{ background:'#fdf8f4', borderRadius:8, padding:12, marginBottom:8 }}>
                  <div style={{ fontSize:12, color:'#a0522d', fontWeight:600, marginBottom:8 }}>Variante {i+1}</div>
                  <div style={s.grid2}>
                    <div><label style={s.label}>Nombre *</label><input style={s.input} placeholder="Ej: Individual" value={v.name} onChange={e=>setVariants(p=>p.map((x,j)=>j===i?{...x,name:e.target.value}:x))}/></div>
                    <div><label style={s.label}>Precio (₡) *</label><input style={s.input} type="number" placeholder="85000" value={v.price} onChange={e=>setVariants(p=>p.map((x,j)=>j===i?{...x,price:e.target.value}:x))}/></div>
                  </div>
                  <div style={s.grid2}>
                    <div><label style={s.label}>Stock inicial</label><input style={s.input} type="number" placeholder="0" value={v.stock} onChange={e=>setVariants(p=>p.map((x,j)=>j===i?{...x,stock:e.target.value}:x))}/></div>
                    <div><label style={s.label}>Stock mínimo</label><input style={s.input} type="number" placeholder="1" value={v.stock_min} onChange={e=>setVariants(p=>p.map((x,j)=>j===i?{...x,stock_min:e.target.value}:x))}/></div>
                  </div>
                  {variants.length > 1 && <button style={s.btn('#fee2e2','#ef4444')} onClick={()=>setVariants(p=>p.filter((_,j)=>j!==i))}>🗑️ Eliminar variante</button>}
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <button style={s.btnPrimary()} onClick={createProduct}>✅ Crear Producto</button>
              <button style={s.btnSec} onClick={()=>{setNewProdModal(false);setProdForm(emptyProduct());setVariants([emptyVariant()]);}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
